import React, { JSX, useEffect } from 'react';
import Icons from 'components/atoms/Icons';
import ProviderNextAvailable from './ProviderNextAvailable';
import { LocationItem } from 'components/types/LocationTypes';
import { myChart } from '../shared/MyChartConfig';
import { getMapsLink } from '../locations/LocationMap';
import { useTracking } from 'lib/tracking';

type ProviderLocationProps = {
  index: number;
  location: LocationItem;
  providerId: string;
  providerName: string;
  deptDays?: { [key: string]: string[] };
  userDistance?: number;
  locationAnchorId?: string;
};

declare global {
  interface Window {
    mychartWidget?: {
      renderWidget: (options: any) => void;
    };
  }
}

const MYCHART_HOST = myChart.host;
const MYCHART_INSTANCE = myChart.instance;
const API_KEY = myChart.apiKey;

const WIDGET_SDK_SRC =
  `https://${MYCHART_HOST}/${MYCHART_INSTANCE}` +
  '/Scripts/lib/Widget/widget_sdk.js';

function loadWidgetScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }

    const script = document.createElement('script');

    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));

    document.head.appendChild(script);
  });
}

const ProviderLocation = (props: ProviderLocationProps): JSX.Element => {
  const {
    index,
    location,
    providerId,
    deptDays,
    userDistance,
    locationAnchorId,
  } = props;

  const { linksource } = useTracking();

  const deptId = location?.fields?.EpicDeptId?.value;
  const visitTypeId = '40045';

  /*
   * Each provider location requires a unique MyChart
   * widget container.
   */
  const containerId = `mychart-container-${providerId}-${deptId}-${index}`;

  const providerLocationUrl = `https://${MYCHART_HOST}/${MYCHART_INSTANCE}`;

  const phone = location?.fields?.Phone?.value;
  const locationId = location?.fields?.LocationId?.value;

  const hasUserDistance =
    userDistance !== null &&
    userDistance !== undefined &&
    String(userDistance).trim() !== '';

  const hasAvailableAppointments = Boolean(
    deptId && deptDays?.[deptId]?.length
  );

  const showUnavailableMessage =
    !hasAvailableAppointments && Boolean(phone);

  useEffect(() => {
    let isMounted = true;

    loadWidgetScript(WIDGET_SDK_SRC)
      .then(() => {
        const tryInit = (): void => {
          if (
            isMounted &&
            window.mychartWidget &&
            document.getElementById(containerId)
          ) {
            window.mychartWidget.renderWidget({
              url: providerLocationUrl,
              apiKey: API_KEY,
              widgetType: 'openscheduling',
              containerId,
              additionalParams: {
                vt: visitTypeId,
                dept: deptId,
                id: providerId,
                linksource,
              },
            });

            return;
          }

          if (isMounted) {
            setTimeout(tryInit, 100);
          }
        };

        tryInit();
      })
      .catch(error => {
        console.error('Unable to initialize the MyChart widget:', error);
      });

    return () => {
      isMounted = false;
    };
  }, [containerId, deptId, providerId, providerLocationUrl, linksource]);

  return (
    <div className="provider-location">
      <div className="inner-section">
        <div className="location-top-row">
          <div className="location-info">
            <div
              className="location-address"
              data-module="LocationMap"
              data-coords={`${parseFloat(
                location?.fields?.Latitude?.value
              )}, ${parseFloat(location?.fields?.Longitude?.value)}`}
              itemScope
              itemProp="hospitalAffiliation"
              itemType="http://schema.org/Hospital"
            >
              <div className="location-heading-wrapper">
                <h3
                  id={locationAnchorId}
                  className="location-name office-location-anchor"
                  itemProp="name"
                >
                  <span className="location-number">{index + 1}.</span>

                  <span className="location-name-text">
                    {location?.fields?.LocationName?.value}
                  </span>
                </h3>
              </div>

              {hasUserDistance && (
                <p className="location-distance">{userDistance}</p>
              )}

              <div className="location-details-wrapper">
                <div className="location-contact-details">
                  <a
                    className="address location-action-link"
                    itemProp="address"
                    itemScope
                    itemType="http://schema.org/PostalAddress"
                    href={getMapsLink({
                      street1: location?.fields?.Street1?.value ?? '',
                      street2: location?.fields?.Street2?.value ?? '',
                      city: location?.fields?.City?.value ?? '',
                      state: location?.fields?.State?.value ?? '',
                      zip: location?.fields?.Zip?.value ?? '',
                    })}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <div className="top-wrapper">
                      <Icons
                        id="icon-location-new"
                        className="section-icon"
                        aria-hidden="true"
                      />

                      <p className="location-address-text">
                        {location?.fields?.Street1?.value}

                        {location?.fields?.Street2?.value
                          ? `, ${location.fields.Street2.value}`
                          : ''}

                        {location?.fields?.Suite?.value
                          ? `, ${location.fields.Suite.value}`
                          : ''}

                        {location?.fields?.City?.value
                          ? `, ${location.fields.City.value}`
                          : ''}

                        {location?.fields?.State?.value
                          ? `, ${location.fields.State.value}`
                          : ''}

                        {location?.fields?.Zip?.value
                          ? ` ${location.fields.Zip.value}`
                          : ''}
                      </p>
                    </div>
                  </a>

                  {phone && (
                    <div className="phone">
                      <Icons
                        id="icon-phone-new"
                        className="section-icon"
                        aria-hidden="true"
                      />

                      <a
                        className="phone-link"
                        href={`tel:${phone}`}
                        itemProp="telephone"
                      >
                        {phone}
                      </a>
                    </div>
                  )}

                  {location?.fields?.PublishToLocal?.value === true && (
                    <div className="details">
                      <Icons
                        id="icon-external-new"
                        className="section-icon"
                        aria-hidden="true"
                      />

                      <a
                        className="details-link"
                        href={`/locations/${
                          location.name
                        }-${locationId?.substring(locationId.length - 6)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Location Details
                      </a>
                    </div>
                  )}
                </div>

                {(hasAvailableAppointments || showUnavailableMessage) && (
                  <div className="location-availability">
                    <Icons
                      id="icon-calendar-location"
                      className="section-icon"
                      aria-hidden="true"
                    />

                    <div className="location-availability-content">
                      {hasAvailableAppointments && (
                        <ProviderNextAvailable
                          deptDays={deptDays}
                          deptId={deptId}
                          providerId={providerId}
                        />
                      )}

                      {showUnavailableMessage && (
                        <p className="not-available">
                          Online scheduling not available for this location.
                          Please call to schedule.
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProviderLocation;
